import { createFileRoute, useNavigate } from "@tanstack/react-router";

export const Route = createFileRoute(
  "/admin/_layout/inventory/delivery-orders"
)({
  component: DeliveryOrdersPage,
});

import { useState, useEffect } from "react";
import { toast } from "sonner";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import { SelectFilter } from "@/components/crud/filters/SelectFilter";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import {
  useFetchDeliveryOrders,
  useDeliveryOrderMutations,
} from "@/hooks/api/inventory/useDeliveryOrders";
import { DeliveryOrderTable } from "@/components/inventory/tables/DeliveryOrderTable";
import { DeliveryOrderFormSheet } from "@/components/inventory/forms/DeliveryOrderFormSheet";
import { DeliveryOrderDetailSheet } from "@/components/inventory/forms/DetailViewSheet";
import type {
  DeliveryOrder,
  DeliveryOrderPayload,
  DeliveryOrderStatus,
} from "@/types/inventory";

const STATUS_OPTIONS = [
  { label: "Pending", value: "PENDING" },
  { label: "Dispatched", value: "DISPATCHED" },
  { label: "Delivered", value: "DELIVERED" },
  { label: "Cancelled", value: "CANCELLED" },
] satisfies { label: string; value: DeliveryOrderStatus }[];

function DeliveryOrdersPage() {
  const navigate = useNavigate();
  const {
    createDeliveryOrder,
    updateDeliveryOrder,
    dispatchDeliveryOrder,
    confirmDelivery,
    cancelDeliveryOrder,
  } = useDeliveryOrderMutations();
  const isMutating =
    createDeliveryOrder.isPending ||
    updateDeliveryOrder.isPending ||
    dispatchDeliveryOrder.isPending ||
    confirmDelivery.isPending ||
    cancelDeliveryOrder.isPending;

  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();
  const [statusFilter, setStatusFilter] = useState<
    DeliveryOrderStatus | undefined
  >(undefined);
  const [showFilters, setShowFilters] = useState(false);
  const [viewTarget, setViewTarget] = useState<DeliveryOrder | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editing, setEditing] = useState<DeliveryOrder | null>(null);
  const [dispatchTarget, setDispatchTarget] = useState<DeliveryOrder | null>(
    null
  );
  const [confirmTarget, setConfirmTarget] = useState<DeliveryOrder | null>(
    null
  );
  const [cancelTarget, setCancelTarget] = useState<DeliveryOrder | null>(null);

  const deliveryOrdersQuery = useFetchDeliveryOrders({
    page,
    page_size: pageSize,
    status: statusFilter,
  });
  const deliveryOrders = deliveryOrdersQuery.data?.results ?? [];
  const count = deliveryOrdersQuery.data?.count ?? 0;

  const isLoading = deliveryOrdersQuery.isPending || isMutating;
  const isFetching = deliveryOrdersQuery.isFetching || isMutating;

  useEffect(() => {
    setPage(1);
  }, [statusFilter, setPage]);

  const resetFilters = () => {
    setStatusFilter(undefined);
    setPage(1);
  };

  const handleOpenSheet = () => {
    setIsSheetOpen(true);
  };

  const handleOpenEditSheet = (do_: DeliveryOrder) => {
    setEditing(do_);
    setIsSheetOpen(true);
  };

  const handleSubmit = async (payload: DeliveryOrderPayload) => {
    if (editing) {
      await updateDeliveryOrder.mutateAsync({
        id: editing.id,
        payload: {
          delivery_date: payload.delivery_date,
          receiver_name: payload.receiver_name,
          receiver_contact: payload.receiver_contact,
          remarks: payload.remarks,
        },
      });
      toast.success("Delivery order updated");
    } else {
      await createDeliveryOrder.mutateAsync(payload);
      toast.success("Delivery order created");
    }
    setIsSheetOpen(false);
    setEditing(null);
  };

  const handleDispatchConfirm = async () => {
    if (!dispatchTarget) return;
    try {
      await dispatchDeliveryOrder.mutateAsync({ id: dispatchTarget.id });
      toast.success(`${dispatchTarget.do_number} dispatched`);
      setDispatchTarget(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleConfirmDeliveryConfirm = async () => {
    if (!confirmTarget) return;
    try {
      await confirmDelivery.mutateAsync({ id: confirmTarget.id });
      toast.success(`${confirmTarget.do_number} delivered — stock updated`);
      setConfirmTarget(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleCancelConfirm = async () => {
    if (!cancelTarget) return;
    try {
      await cancelDeliveryOrder.mutateAsync({ id: cancelTarget.id });
      toast.success(`${cancelTarget.do_number} cancelled`);
      setCancelTarget(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Delivery Orders"
          description="Manage customer delivery orders"
          onToggleFilter={() => setShowFilters((prev) => !prev)}
          onAdd={handleOpenSheet}
          addLabel="New Delivery Order"
          isLoading={isFetching}
        />

        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <SelectFilter
            placeholder="Status"
            options={STATUS_OPTIONS}
            value={statusFilter}
            onChange={setStatusFilter}
          />
        </FiltersPanel>

        <DeliveryOrderTable
          deliveryOrders={deliveryOrders}
          isLoading={isLoading}
          onNameClick={(d) =>
            navigate({
              to: "/admin/inventory/delivery-orders/$id",
              params: { id: String(d.id) },
            })
          }
          onView={(d) => setViewTarget(d)}
          onEdit={handleOpenEditSheet}
          onDispatch={(d) => setDispatchTarget(d)}
          onConfirmDelivery={(d) => setConfirmTarget(d)}
          onCancel={(d) => setCancelTarget(d)}
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={(p) => setPage(p)}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />

        <DeliveryOrderFormSheet
          isOpen={isSheetOpen}
          onClose={() => {
            setIsSheetOpen(false);
            setEditing(null);
          }}
          onSubmit={handleSubmit}
          isLoading={isMutating}
          editing={editing}
        />

        {/* Dispatch confirm */}
        <DeleteConfirmDialog
          isOpen={!!dispatchTarget}
          onClose={() => setDispatchTarget(null)}
          onConfirm={handleDispatchConfirm}
          title="Dispatch Delivery Order"
          description={`Dispatch ${dispatchTarget?.do_number}? The sales order will move to Shipped.`}
          itemName={dispatchTarget?.do_number}
          isLoading={isMutating}
          destructive={false}
          confirmLabel="Dispatch"
          loadingLabel="Dispatching..."
        />

        {/* Confirm delivery */}
        <DeleteConfirmDialog
          isOpen={!!confirmTarget}
          onClose={() => setConfirmTarget(null)}
          onConfirm={handleConfirmDeliveryConfirm}
          title="Confirm Delivery"
          description={`Mark ${confirmTarget?.do_number} as delivered? This will post a SALE_OUT stock transaction and cannot be undone.`}
          itemName={confirmTarget?.do_number}
          isLoading={isMutating}
          destructive={false}
          confirmLabel="Confirm Delivery"
          loadingLabel="Confirming..."
        />

        {/* Cancel confirm */}
        <DeleteConfirmDialog
          isOpen={!!cancelTarget}
          onClose={() => setCancelTarget(null)}
          onConfirm={handleCancelConfirm}
          title="Cancel Delivery Order"
          description={`Cancel ${cancelTarget?.do_number}? This cannot be undone.`}
          itemName={cancelTarget?.do_number}
          isLoading={isMutating}
          destructive
        />
      </div>

      <DeliveryOrderDetailSheet
        isOpen={!!viewTarget}
        onClose={() => setViewTarget(null)}
        order={viewTarget}
      />
    </SidebarInset>
  );
}
